import React, { useLayoutEffect, useState } from 'react'
import { VestigingEnum } from 'shared/models/Enum'
import { IOrderType } from 'shared/models/IOrderType'
import {
  comparableDate,
  formatDate,
  selectDay,
} from 'shared/service/helpers/DateUtilities'
import { IAfleverafspraakGroepKoppeling } from '../../../shared/models/IAfleverafspraakGroepKoppeling'
import { IAfleverafspraakLocatieKoppeling } from '../../../shared/models/IAfleverafspraakLocatieKoppeling'
import { IKlant } from '../../../shared/models/IKlant'
import { IKlantAfleverafspraak } from '../../../shared/models/IKlantAfleverafspraak'

export type RowType =
  | IKlantAfleverafspraak
  | IAfleverafspraakGroepKoppeling
  | IAfleverafspraakLocatieKoppeling

export function isAgreement(item: RowType): item is IKlantAfleverafspraak {
  return item != null && 'afleverafspraakReferentie' in item
}

export function isLocationLink(
  item: RowType
): item is IAfleverafspraakLocatieKoppeling {
  return item != null && 'afleverafspraakLocatieKoppelingId' in item
}

export function isGroupLink(
  item: RowType
): item is IAfleverafspraakGroepKoppeling {
  return item != null && 'afleverafspraakGroepKoppelingId' in item
}

interface IProps {
  customer: IKlant
  copyRow: IAfleverafspraakLocatieKoppeling
  possibleOrderTypes: IOrderType[]
  onRowSelected: (row: RowType) => void
}

export default (props: IProps) => {
  const [activeRow, setActiveRow] = useState<RowType>()
  const [mouseAgreement, setMouseAgreement] = useState<number>()

  useLayoutEffect(() => {
    const singleAgreement = tryGetSingleAgreement(props.customer)
    const singleLink = tryGetSingleLink(singleAgreement)
    if (!!singleLink) {
      selectRow(singleLink)
    } else if (!!singleAgreement) {
      selectRow(singleAgreement)
    } else {
      selectRow(null)
    }
  }, [props.customer])

  const selectRow = (row: RowType) => {
    setActiveRow(row)
    props.onRowSelected(row)
  }

  const activeRowIsAgreement = (): boolean => {
    return isAgreement(activeRow)
  }

  const activeRowIsLocationLink = (): boolean => {
    return isLocationLink(activeRow)
  }

  const activeRowIsGroupLink = (): boolean => {
    return isGroupLink(activeRow)
  }

  const activeRowAsAgreement = (): IKlantAfleverafspraak => {
    if (activeRowIsAgreement) {
      return activeRow as IKlantAfleverafspraak
    } else {
      return null
    }
  }

  const activeRowAsLocationLink = (): IAfleverafspraakLocatieKoppeling => {
    if (activeRowIsLocationLink) {
      return activeRow as IAfleverafspraakLocatieKoppeling
    } else {
      return null
    }
  }

  const activeRowAsGroupLink = (): IAfleverafspraakGroepKoppeling => {
    if (activeRowIsGroupLink) {
      return activeRow as IAfleverafspraakGroepKoppeling
    } else {
      return null
    }
  }

  const tryGetSingleAgreement = (customer: IKlant): IKlantAfleverafspraak => {
    if (
      !!customer &&
      !!customer.klantAfleverafspraken &&
      customer.klantAfleverafspraken.length === 1
    ) {
      return customer.klantAfleverafspraken[0]
    }

    return null
  }

  const tryGetSingleLink = (
    agreement: IKlantAfleverafspraak
  ): IAfleverafspraakLocatieKoppeling => {
    if (
      !!agreement &&
      !!agreement.afleverafspraakLocatieKoppelingen &&
      agreement.afleverafspraakLocatieKoppelingen.length === 1 &&
      (!agreement.groepKoppelingen || agreement.groepKoppelingen.length === 0)
    ) {
      return agreement.afleverafspraakLocatieKoppelingen[0]
    }

    return null
  }

  const isMouseOverForCopy = (id: number) => {
    return (
      !!props.copyRow &&
      (mouseAgreement === id ||
        (!!activeRowIsAgreement() &&
          activeRowAsAgreement().klantAfleverafspraakId === id))
    )
  }

  const mouseEnterAgreement = (id: number) => {
    setMouseAgreement(id)
  }

  const mouseLeaveAgreement = (id: number) => {
    if (mouseAgreement === id) {
      setMouseAgreement(null)
    }
  }

  const getSortedRows = (data: IKlantAfleverafspraak[]) => {
    return data.sort((a, b) => (a.naam > b.naam ? 1 : -1))
  }

  const getSortedLinks = (links: IAfleverafspraakLocatieKoppeling[]) => {
    return links.sort((a, b) =>
      comparableDate(formatDate(a.ingangsDatumtijd)) >
      comparableDate(formatDate(b.ingangsDatumtijd))
        ? 1
        : -1
    )
  }

  const getSortedGroups = (groups: IAfleverafspraakGroepKoppeling[]) => {
    const grouped: {
      klantGroepId: number
      ingangsDatum: Date
      koppelingen: IAfleverafspraakGroepKoppeling[]
    }[] = []
    groups.forEach(g => {
      const items = grouped.find(r => r.klantGroepId === g.klantGroepId)
      if (!items) {
        grouped.push({
          klantGroepId: g.klantGroepId,
          ingangsDatum: g.ingangsDatum,
          koppelingen: [g],
        })
      } else {
        if (items.ingangsDatum > g.ingangsDatum) {
          items.ingangsDatum = g.ingangsDatum
        }
        items.koppelingen.push(g)
      }
    })

    const sorted = sortGrouped(grouped)
    sorted.forEach(g => {
      g.koppelingen = sortGroups(g.koppelingen)
    })

    return sorted
  }

  const sortGroups = (groups: IAfleverafspraakGroepKoppeling[]) => {
    return groups.sort((a, b) =>
      comparableDate(formatDate(a.ingangsDatum)) >
      comparableDate(formatDate(b.ingangsDatum))
        ? 1
        : -1
    )
  }

  const sortGrouped = (
    groups: {
      klantGroepId: number
      ingangsDatum: Date
      koppelingen: IAfleverafspraakGroepKoppeling[]
    }[]
  ) => {
    return groups.sort((a, b) =>
      comparableDate(formatDate(a.ingangsDatum)) >
      comparableDate(formatDate(b.ingangsDatum))
        ? 1
        : -1
    )
  }

  const getOrderTypesDisplay = (orderTypes: string): string => {
    if (!props.possibleOrderTypes) {
      return orderTypes
    }

    const types = orderTypes
      .split(',')
      .map(t => +t)
      .sort((a, b) => a - b)

    if (props.possibleOrderTypes.length != types.length) {
      return orderTypes
    } else if (
      props.possibleOrderTypes.filter(t => !types.some(o => o === t.value))
        .length > 0
    ) {
      return orderTypes
    } else {
      return 'Alle'
    }
  }

  const getDaysDisplay = (days: string): string => {
    const dayArray = days
      .split(',')
      .map(d => +d)
      .sort((a, b) => (a === 0 ? 1 : a - b))

    if (dayArray.length === 7) {
      return 'Alle'
    } else {
      return dayArray.map(d => selectDay(d)).join(',')
    }
  }

  const getActiveRowClass = (): string => {
    return 'tddata table-primary'
  }

  const getRowClass = (index: number): string => {
    return index % 2 !== 0 ? 'tddata table-secondary' : 'tddata'
  }

  const getAgreementClass = (id: number, index: number): string => {
    return activeRowIsAgreement() &&
      activeRowAsAgreement().klantAfleverafspraakId === id
      ? getActiveRowClass()
      : getRowClass(index)
  }

  const getLocationClass = (id: number, index: number): string => {
    if (
      activeRowAsLocationLink() &&
      activeRowAsLocationLink().afleverafspraakLocatieKoppelingId === id
    ) {
      return getActiveRowClass()
    } else if (
      !!props.copyRow &&
      props.copyRow.afleverafspraakLocatieKoppelingId === id
    ) {
      return 'tddata table-warning'
    } else {
      return getRowClass(index)
    }
  }

  const getGroupClass = (id: number, index: number): string => {
    return activeRowIsGroupLink() &&
      activeRowAsGroupLink().afleverafspraakGroepKoppelingId === id
      ? getActiveRowClass()
      : getRowClass(index)
  }

  const renderRows = (deliveryAgreements: IKlantAfleverafspraak[]) => {
    return getSortedRows(deliveryAgreements).map((x, xIndex) => {
      const result = []

      if (
        !!x.afleverafspraakLocatieKoppelingen &&
        x.afleverafspraakLocatieKoppelingen.length > 0
      ) {
        ;[].push.apply(
          result,
          getSortedLinks(x.afleverafspraakLocatieKoppelingen).map((y, index) =>
            renderLocationRow(y, x, index, xIndex)
          )
        )
      } else {
        result.push(renderEmptyRow(x, xIndex))
      }

      if (!!x.groepKoppelingen && x.groepKoppelingen.length > 0) {
        ;[].push.apply(
          result,
          getSortedGroups(x.groepKoppelingen).map(y =>
            renderGroupRow(y, x, xIndex)
          )
        )
      }

      return result
    })
  }

  const renderLocationRow = (
    link: IAfleverafspraakLocatieKoppeling,
    agreement: IKlantAfleverafspraak,
    index: number,
    agreementIndex: number
  ) => {
    return (
      <tr
        key={link.afleverafspraakLocatieKoppelingId}
        className={getLocationClass(
          link.afleverafspraakLocatieKoppelingId,
          agreementIndex
        )}
        onClick={() => selectRow(link)}
      >
        <td>{index === 0 ? agreement.afleverafspraakReferentie : ''}</td>
        <td>{index === 0 ? agreement.naam : ''}</td>
        <td></td>
        <td>Alle</td>
        <td>Alle</td>
        <td>{!!link.shipToLocationGln ? link.shipToLocationGln.locatieGlnCode : ''}</td>
        <td>{!!link.shipToLocationGln ? link.shipToLocationGln.locatieGlnNaam : ''}</td>
        <td>
          {!!link.shipToLocationGln
            ? VestigingEnum[link.shipToLocationGln.vestigingCode]
            : ''}
        </td>
        <td>
          {!!link.ingangsDatumtijd ? formatDate(link.ingangsDatumtijd) : ''}
        </td>
        <td>{!!link.eindDatumtijd ? formatDate(link.eindDatumtijd) : ''}</td>
      </tr>
    )
  }

  const renderEmptyRow = (
    agreement: IKlantAfleverafspraak,
    agreementIndex: number
  ) => {
    return (
      <tr
        key={`A${agreement.klantAfleverafspraakId}`}
        className={getAgreementClass(
          agreement.klantAfleverafspraakId,
          agreementIndex
        )}
        onClick={() => selectRow(agreement)}
        onMouseEnter={() =>
          mouseEnterAgreement(agreement.klantAfleverafspraakId)
        }
        onMouseLeave={() =>
          mouseLeaveAgreement(agreement.klantAfleverafspraakId)
        }
      >
        <td>{agreement.afleverafspraakReferentie}</td>
        <td>{agreement.naam}</td>
        <td></td>
        <td className='font-weight-light font-italic'>
          {isMouseOverForCopy(agreement.klantAfleverafspraakId) ? 'Alle' : ''}
        </td>
        <td className='font-weight-light font-italic'>
          {isMouseOverForCopy(agreement.klantAfleverafspraakId) ? 'Alle' : ''}
        </td>
        <td className='font-weight-light font-italic'>
          {isMouseOverForCopy(agreement.klantAfleverafspraakId)
            ? props.copyRow.shipToLocationGln.locatieGlnCode
            : ''}
        </td>
        <td className='font-weight-light font-italic'>
          {isMouseOverForCopy(agreement.klantAfleverafspraakId)
            ? props.copyRow.shipToLocationGln.locatieGlnNaam
            : ''}
        </td>
        <td className='font-weight-light font-italic'>
          {isMouseOverForCopy(agreement.klantAfleverafspraakId)
            ? VestigingEnum[props.copyRow.shipToLocationGln.vestigingCode]
            : ''}
        </td>
        <td className='font-weight-light font-italic'>
          {isMouseOverForCopy(agreement.klantAfleverafspraakId)
            ? formatDate(props.copyRow.ingangsDatumtijd)
            : ''}
        </td>
        <td className='font-weight-light font-italic'>
          {isMouseOverForCopy(agreement.klantAfleverafspraakId)
            ? formatDate(props.copyRow.eindDatumtijd)
            : ''}
        </td>
      </tr>
    )
  }

  const renderGroupRow = (
    grouped: {
      klantGroepId: number
      ingangsDatum: Date
      koppelingen: IAfleverafspraakGroepKoppeling[]
    },
    agreement: IKlantAfleverafspraak,
    agreementIndex: number
  ) => {
    return grouped.koppelingen.map((group, index) => {
      return (
        <tr
          key={`G${group.afleverafspraakGroepKoppelingId}`}
          className={getGroupClass(
            group.afleverafspraakGroepKoppelingId,
            agreementIndex
          )}
          onClick={() => selectRow(group)}
        >
          <td></td>
          <td></td>
          <td
            className='mw-200 overflow-hidden'
            title={group.klantGroep.klantGroepOmschrijving}
          >
            {group.klantGroep.klantGroepOmschrijving}
          </td>
          <td>{getOrderTypesDisplay(group.orderTypes)}</td>
          <td>{getDaysDisplay(group.dagen)}</td>
          <td>{VestigingEnum[group.klantGroep.vestigingCode.valueOf()]}</td>
          <td>{formatDate(group.ingangsDatum)}</td>
          <td>{formatDate(group.eindDatum)}</td>
        </tr>
      )
    })
  }

  return (
    !!props.customer && (
      <table className='table table-hover'>
        <thead>
          <tr>
            <th colSpan={10}>Gekoppelde adressen</th>
          </tr>
        </thead>
        <tbody>
          {activeRowIsLocationLink() && !activeRowAsLocationLink().shipToLocationGln && (
            <tr>
              <td colSpan={10}>
                <div className='alert alert-warning'>
                  Koppeling wordt pas opgeslagen na het kiezen van een locatie.
                </div>
              </td>
            </tr>
          )}
          <tr className='tablesubheader'>
            <td>AfleverafspraakReferentie</td>
            <td>AfleverafspraakNaam</td>
            <td>Afleverlocatiegroep</td>
            <td>Ordertype</td>
            <td>Dagen</td>
            <td>Vestiging</td>
            <td>Ingangsdatum</td>
            <td>Einddatum</td>
          </tr>
          {renderRows(props.customer.klantAfleverafspraken)}
        </tbody>
      </table>
    )
  )
}
